import { Cell } from 'api/owners/owners.types';
import { JobIdResponse } from 'api/jobs/jobs.types';
import { UpdateGroupResponse, CreateGroupResponse, BatchPermissionResponse } from 'api/groups/groups.types';

/**
 * Utility functions for group update operations and async job detection
 */

/**
 * Type guard to check if group create response is a job response (async operation)
 */
export const isCreateGroupJobResponse = (response: CreateGroupResponse): response is JobIdResponse => {
  return 'jobId' in response;
};

/**
 * Type guard to check if group create response is a regular create response (sync operation)
 */
export const isCreateGroupSyncResponse = (response: CreateGroupResponse): response is { id: string } => {
  return 'id' in response && !('jobId' in response);
};

/**
 * Type guard to check if response is a job response (async operation)
 */
export const isJobResponse = (response: UpdateGroupResponse): response is JobIdResponse => {
  return 'jobId' in response;
};

/**
 * Type guard to check if response is a regular group update response (sync operation)
 */
export const isGroupUpdateResponse = (response: UpdateGroupResponse): response is { id: string } => {
  return 'id' in response && !('jobId' in response);
};

/**
 * Detects if komorka changes will trigger async processing
 * Compares original vs new komorka assignments
 *
 * @param originalCells - Current komorka assignments
 * @param newCells - New komorka assignments from form
 * @returns true if changes will trigger async processing (add/delete operations)
 */
export const detectsKomorkaChanges = (originalCells: Cell[] = [], newCells: Cell[] = []): boolean => {
  // Convert to sets for efficient comparison
  const originalIds = new Set(originalCells.map((cell) => cell.id));
  const newIds = new Set(newCells.map((cell) => cell.id));

  // Check for additions (new cells not in original)
  const hasAdditions = newCells.some((cell) => !originalIds.has(cell.id));

  // Check for deletions (original cells not in new)
  const hasDeletions = originalCells.some((cell) => !newIds.has(cell.id));

  return hasAdditions || hasDeletions;
};

/**
 * Gets a description of komorka changes for user feedback
 *
 * @param originalCells - Current komorka assignments
 * @param newCells - New komorka assignments from form
 * @param formatMessage - Optional translation function for internationalization
 * @returns Description of changes or null if no changes
 */
export const getKomorkaChangesDescription = (
  originalCells: Cell[] = [],
  newCells: Cell[] = [],
  formatMessage?: (messageDescriptor: { id: string }, values?: Record<string, unknown>) => string,
): string | null => {
  const originalIds = new Set(originalCells.map((cell) => cell.id));
  const newIds = new Set(newCells.map((cell) => cell.id));

  const additions = newCells.filter((cell) => !originalIds.has(cell.id));
  const deletions = originalCells.filter((cell) => !newIds.has(cell.id));

  if (additions.length === 0 && deletions.length === 0) {
    return null;
  }

  const changes: string[] = [];

  if (additions.length > 0) {
    if (formatMessage) {
      // Use translation key for internationalized version
      changes.push(formatMessage({ id: 'job.background.processing.department.add' }, { count: additions.length }));
    } else {
      // Fallback to hardcoded English
      changes.push(`Adding ${additions.length} department${additions.length === 1 ? '' : 's'}`);
    }
  }

  if (deletions.length > 0) {
    if (formatMessage) {
      // Use translation key for internationalized version
      changes.push(formatMessage({ id: 'job.background.processing.department.remove' }, { count: deletions.length }));
    } else {
      // Fallback to hardcoded English
      changes.push(`Removing ${deletions.length} department${deletions.length === 1 ? '' : 's'}`);
    }
  }

  return changes.join(' and ');
};

/**
 * Estimates the number of PKDs that will be affected by komorka changes
 * This is used for user messaging about background processing time
 *
 * @param addedCells - Cells being added
 * @param removedCells - Cells being removed
 * @returns Estimated number of affected PKDs
 */
export const estimateAffectedPkds = (addedCells: Cell[] = [], removedCells: Cell[] = []): number => {
  // Rough estimate based on typical department sizes
  // This could be enhanced with actual PKD counts from the API in the future
  const avgPkdsPerCell = 25; // Conservative estimate

  const totalAffectedCells = addedCells.length + removedCells.length;
  return totalAffectedCells * avgPkdsPerCell;
};

/**
 * Type guard to check if batch permission response is a job response (async operation)
 */
export const isBatchPermissionJobResponse = (response: BatchPermissionResponse): response is JobIdResponse => {
  return 'jobId' in response;
};

/**
 * Type guard to check if batch permission response is a regular batch response (sync operation)
 */
export const isBatchPermissionSyncResponse = (
  response: BatchPermissionResponse,
): response is { successful: number; failed: number; errors?: string[] } => {
  return 'successful' in response && 'failed' in response;
};

/**
 * Estimates the number of PKDs that will be affected by permission changes
 * This is used for determining if operation should be async
 *
 * @param groupIds - Array of group IDs being modified
 * @param avgPkdsPerGroup - Average PKDs per group (default: 50)
 * @returns Estimated number of affected PKDs
 */
export const estimatePermissionAffectedPkds = (groupIds: string[] = [], avgPkdsPerGroup: number = 50): number => {
  return groupIds.length * avgPkdsPerGroup;
};

/**
 * Determines if permission operation should be processed asynchronously
 * Based on estimated number of affected PKDs
 *
 * @param groupIds - Array of group IDs being modified
 * @param threshold - PKD threshold for async processing (default: 100)
 * @returns true if operation should be async
 */
export const shouldProcessPermissionAsync = (groupIds: string[] = [], threshold: number = 100): boolean => {
  const estimatedPkds = estimatePermissionAffectedPkds(groupIds);
  return estimatedPkds >= threshold;
};
